import React, { ReactElement } from "react";

import { I18n } from "i18n-js";
import * as locales from "../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

import AdminNavSection from "./adminNavSection";
import SuperAdminNavSection from "./superAdminNavSection";
import ChargebeePortalLink from "../chargebeePortalLink";

import { Subscription } from "../../interfaces/interfaces";

interface NavProps {
  chargebeeSite: string;
  homeUrl: string;
  isDemo: boolean;
  isDemoing: boolean;
  isMobile: boolean;
  isSpoofing: boolean;
  isSuperAdmin: boolean;
  isTrialing: boolean;
  location: string;
  logoFilename: string;
  navigationShrunk: boolean;
  pendingAwardsClaims: number;
  permissions: any;
  productTier: string;
  quickSpoofCompanies: any;
  rewardsUrl: string;
  subscription: Subscription;
  unresolvedCount: number;
  user: {
    id: string;
    profilePic: string;
    name: string;
  };
  userManagmentIntegrationURL?: string;
}

export default class Nav extends React.Component<NavProps, any> {
  state = {
    profileOpen: false,
    expandedGroup: null,
  };

  render(): ReactElement {
    return (
      <nav
        className={`application-nav hidden-phone left-nav-redesign ${this.getClasses()}`}
      >
        <div className="home-links">
          <a className="logo" href={this.props.homeUrl}>
            <img alt="logo" src={this.props.logoFilename} />
          </a>
          <a className="go-home-button" href={this.props.homeUrl}>
            {i18n.translate("navigation.go_home")}
          </a>
        </div>
        <div className="link-groups">
          <AdminNavSection
            chargebeeSite={this.props.chargebeeSite}
            expandedGroup={this.state.expandedGroup}
            handleGroupClick={this.handleGroupClick}
            handleInitialLoad={this.handleInitialLoad}
            isDemo={this.props.isDemo}
            isMobile={this.props.isMobile}
            isTrialing={this.props.isTrialing}
            location={this.props.location}
            navigationShrunk={this.props.navigationShrunk}
            pendingAwardsClaims={this.props.pendingAwardsClaims}
            permissions={this.props.permissions}
            productTier={this.props.productTier}
            subscription={this.props.subscription}
            unresolvedCount={this.props.unresolvedCount}
            userManagmentIntegrationURL={this.props.userManagmentIntegrationURL}
          />
          <SuperAdminNavSection
            expandedGroup={this.state.expandedGroup}
            handleGroupClick={this.handleGroupClick}
            handleInitialLoad={this.handleInitialLoad}
            isDemoing={this.props.isDemoing}
            isSuperAdmin={this.props.isSuperAdmin}
            location={this.props.location}
            navigationShrunk={this.props.navigationShrunk}
            permissions={this.props.permissions}
            quickSpoofCompanies={this.props.quickSpoofCompanies}
          />
          {this.renderTrialWarningSection()}
        </div>
      </nav>
    );
  }

  renderUserLogout = () => {
    const logoutUrl = "/users/sign_out";

    return (
      <a href={logoutUrl}>
        <i className="fa fa-sign-out-alt" />
        {i18n.translate("menu.logout")}
      </a>
    );
  };

  renderSuperAdminSection = () => {
    if (this.props.isSuperAdmin || this.props.isSpoofing) {
      return (
        <>
          {this.renderStopSpoofing()}
          {this.renderBonusReasons()}
          {this.renderSuperAdminControls()}
        </>
      );
    }

    return null;
  };

  renderStopSpoofing = () => {
    if (this.props.isSpoofing) {
      return (
        <li>
          <a href="/super_admin/spoof/stop_spoofing">
            <i className="fa fa-user-secret" />
            Stop spoofing
          </a>
        </li>
      );
    }

    return null;
  };

  renderBonusReasons = () => {
    if (!this.props.isDemoing && this.props.isSpoofing) {
      return (
        <li>
          <a
            href="/super_admin/snoopings/new"
            data-toggle="modal"
            data-target="#snooping-form"
            data-keyboard
          >
            <i className="fa fa-comments" />
            Bonus Reasons
          </a>
        </li>
      );
    }

    return null;
  };

  renderSuperAdminControls = () => {
    if (!this.props.isDemoing) {
      return (
        <li>
          <a href="/users/toggle_super_admin_controls" data-method="PATCH">
            <i className="fa fa-toggle-on" />
            SuperAdmin
          </a>
        </li>
      );
    }

    return null;
  };

  handleOpenProfile = () => {
    this.setState((prevState) => {
      return {
        profileOpen: !prevState.profileOpen,
      };
    });
  };

  getProfileClasses = () => {
    return this.state.profileOpen ? "profile active" : "profile";
  };

  renderTrialWarningSection = () => {
    if (
      this.props.subscription.showBottomFreeTrialWarning &&
      !this.props.isMobile
    ) {
      return (
        <div className="sidebar-cta">
          {this.renderTrialCopy()}
          <ChargebeePortalLink
            id="add-payment-button"
            site={this.props.chargebeeSite}
            text="Add payment"
          />
        </div>
      );
    }

    return null;
  };

  renderTrialCopy = () => {
    if (this.props.subscription.inTrial) {
      return (
        <>
          {i18n.translate("shared.header.you_have")}
          <span className="days-left">
            {` ${this.props.subscription.daysLeft} ${i18n.translate(
              "shared.header.days"
            )} `}
          </span>
          {i18n.translate("shared.header.left_in_trial")}
        </>
      );
    }

    return <>{i18n.translate("shared.header.your_trial_has_ended")}</>;
  };

  handleGroupClick = (groupId) => {
    if (groupId === this.state.expandedGroup) {
      this.setState({
        expandedGroup: null,
      });
    } else {
      this.setState({
        expandedGroup: groupId,
      });
    }
  };

  handleInitialLoad = (defaultGroup) => {
    if (this.state.expandedGroup === null) {
      this.setState({
        expandedGroup: defaultGroup,
      });
    }
  };

  getClasses = () => {
    if (this.props.navigationShrunk) return "shrunk";
  };
}
